import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { accrualHistoryQueryKeys } from '@/hooks/leave/useAccrualHistory';
import { timesheetQueryKeys } from '@/hooks/timesheets/queryKeys';
import { leaveQueryKeys } from '@/hooks/leave/queryKeys';

interface CreatePayrollRunParams {
  startDate: string;
  endDate: string;
  selectedEmployeeIds: string[];
}

interface GeneratePayrollParams {
  payrollRunId: string;
  startDate: string;
  endDate: string;
  selectedEmployeeIds: string[];
}

async function createPayrollRun(params: CreatePayrollRunParams) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile } = await supabase
    .from('profiles')
    .select('company_id, id')
    .eq('user_id', user.id)
    .single();

  if (!profile) throw new Error('Profile not found');

  // Second precision + short suffix: run_number is unique per company at the
  // database level, so minute precision would collide on rapid consecutive runs.
  const now = new Date();
  const suffix = Math.random().toString(36).slice(2, 5).toUpperCase();
  const runNumber = `PR-${format(now, 'yyyyMMdd')}-${format(now, 'HHmmss')}-${suffix}`;

  const { data, error } = await supabase
    .from('payroll_runs')
    .insert({
      company_id: profile.company_id,
      run_number: runNumber,
      start_date: params.startDate,
      end_date: params.endDate,
      // Created directly as `processing`: the previous draft → update → re-read
      // sequence left orphan rows whenever the client failed mid-sequence.
      status: 'processing',
      total_employees_selected: params.selectedEmployeeIds.length,
      created_by: profile.id,
      metadata: {
        selected_employee_ids: params.selectedEmployeeIds,
        last_heartbeat_at: new Date().toISOString(),
      },
    })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error(
        'Another payroll run for this company and pay period is already processing. Wait for it to finish, or cancel the stuck run from Payroll History.'
      );
    }
    throw error;
  }
  return data;
}

async function generatePayrollWithRun(params: GeneratePayrollParams) {
  // The run is already `processing` and already carries the selected IDs, so
  // no status update / metadata re-read is needed here.
  const { data, error } = await supabase.functions.invoke('generate-payroll', {
    body: {
      start_date: params.startDate,
      end_date: params.endDate,
      payroll_run_id: params.payrollRunId,
      selected_employee_ids: params.selectedEmployeeIds,
    },
  });

  if (error) {
    // Client-side invocation failure: don't leave the run stuck in `processing`.
    await supabase
      .from('payroll_runs')
      .update({ status: 'failed' })
      .eq('id', params.payrollRunId)
      .eq('status', 'processing');
    throw error;
  }

  return data;
}

export const usePayrollMutations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createRun = useMutation({
    mutationFn: createPayrollRun,
  });

  const generatePayroll = useMutation({
    mutationFn: generatePayrollWithRun,
    onSuccess: (data, variables) => {
      // Surface service charge distribution failure immediately,
      // independent of the run-number fetch that follows.
      if (data.service_charge_warning) {
        toast({
          title: 'Service Charge Warning',
          description: data.service_charge_warning,
          variant: 'destructive',
          duration: 15000,
        });
      }

      // Surface accrual history batch failure. No re-run should be attempted
      // without administrator review — accrual_history has no idempotency constraint.
      if (data.accrual_history_warning) {
        toast({
          title: 'Accrual History Not Recorded',
          description: data.accrual_history_warning,
          variant: 'destructive',
          duration: 15000,
        });
      }

      // Surface employees whose payslips were rolled back due to line item insert failures.
      if (data.failed_count > 0) {
        const names = (data.failed_employees as Array<{ name: string }> | undefined)
          ?.map(e => e.name)
          .join(', ');
        toast({
          title: `${data.failed_count} Employee(s) Failed Payroll Processing`,
          description: `Payslips were rolled back for: ${names}. Check edge function logs for details, then re-run payroll for these employees.`,
          variant: 'destructive',
          duration: 15000,
        });
      }

      // Surface zero-pay employees: no payslip was created for these employees
      // because they have no active compensation rules. Operator must fix configuration.
      if (data.zero_pay_count > 0) {
        const names = (data.zero_pay_employees as Array<{ name: string }> | undefined)
          ?.map(e => e.name)
          .join(', ');
        toast({
          title: `${data.zero_pay_count} Employee(s) Have No Compensation Rules`,
          description: `No payslip was created for: ${names}. Assign active compensation rules before re-running payroll for these employees.`,
          variant: 'destructive',
          duration: 15000,
        });
      }

      // Fetch the run to get the run_number for toast.
      // Wrap in Promise.resolve to get a full Promise with .catch() support,
      // since Supabase's .then() returns a PromiseLike.
      Promise.resolve(
        supabase
          .from('payroll_runs')
          .select('run_number')
          .eq('id', variables.payrollRunId)
          .single()
      ).then(({ data: run }) => {
          const skippedCount = data.skipped_count || 0;
          const createdCount = data.payslips_created || 0;
          const runLabel = run?.run_number || variables.payrollRunId;
          
          if (createdCount === 0 && skippedCount > 0) {
            toast({
              title: 'No Payslips Created',
              description: `Run ${runLabel}: All ${skippedCount} employee(s) already have payslips for this period`,
              variant: 'destructive',
            });
          } else if (skippedCount > 0) {
            toast({
              title: 'Payroll Generated with Skipped Employees',
              description: `Run ${runLabel}: ${createdCount} payslip(s) created, ${skippedCount} skipped (existing payslips)`,
            });
          } else {
            toast({
              title: 'Payroll Generated Successfully',
              description: `Run ${runLabel}: ${createdCount} payslip(s) created`,
            });
          }
        }).catch(() => {
          // Fallback: show result toast even if run-number lookup fails
          const createdCount = data.payslips_created || 0;
          toast({
            title: 'Payroll Generated',
            description: `${createdCount} payslip(s) created`,
          });
        });

      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      // Invalidate accrual history since payroll processing creates accrual entries
      queryClient.invalidateQueries({ queryKey: accrualHistoryQueryKeys.all });
      // Invalidate timesheet queries so payroll verification status updates immediately
      // This handles same-user navigation from Payroll Management to Team Timesheets
      queryClient.invalidateQueries({ queryKey: timesheetQueryKeys.all });
      // Invalidate leave queries so payroll verification status updates immediately
      // This handles same-user navigation from Payroll Management to Leave Approvals
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.companyRequests() });
    },
    onError: (error: Error) => {
      toast({
        title: 'Payroll Generation Failed',
        description: error.message || 'An error occurred while generating payroll',
        variant: 'destructive',
      });
    },
  });

  return {
    createRun,
    generatePayroll,
  };
};
